import { useCallback, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { EyeSlashIcon } from '@heroicons/react/24/outline';

import { showToast } from '@restworld/ui-ds-base';

import { selectedJobApplicationAtom, useCandidateStateChangeModal } from '../../../../employer-job-listing.atoms';
import { useUpdateJobApplicationMutation } from '../../../../hooks/use-update-job-application';
import { usePreselectionRejectedJobApplicationsQueryKey } from '../../../../hooks/use-employer-job-applications';
import { CandidateModalStatusChangeScreen } from './candidate-state-change-confirmation';

interface HideCandidateConfirmationProps {
  workerId: string;
  jobPositionId: string;
}

export function HideCandidateConfirmation({ workerId, jobPositionId }: HideCandidateConfirmationProps) {
  const { t } = useTranslation('job-positions-page');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const { closeCandidateModal } = useCandidateStateChangeModal();
  const preselectionRejectedQueryKey = usePreselectionRejectedJobApplicationsQueryKey();

  const { mutate, isPending, isSuccess } = useUpdateJobApplicationMutation();

  const candidateName = selectedJobApplication?.worker?.name ?? '';

  const handleSubmit = useCallback(() => {
    mutate({
      params: { hidden: true },
      workerId,
      jobPositionId,
    });
  }, [jobPositionId, mutate, workerId]);

  useEffect(() => {
    if (!isSuccess) return;
    queryClient.invalidateQueries({ queryKey: preselectionRejectedQueryKey });
    showToast(tCommon('candidate_visibility_updated'), 'success', { autoClose: 3000, position: 'top-right' });
    closeCandidateModal();
  }, [closeCandidateModal, isSuccess, preselectionRejectedQueryKey, queryClient, tCommon]);

  return (
    <CandidateModalStatusChangeScreen
      topIcon={<EyeSlashIcon className="h-6 w-6 text-neutral-700" />}
      defaultText={t('hide_candidate_confirmation_title', { name: candidateName })}
      successText={t('hide_candidate_confirmation_success', { name: candidateName })}
      isCompletedSuccessfully={isSuccess}
      isLoading={isPending}
      handleSubmit={handleSubmit}
      bottomConfirmationText={t('hide_candidate_confirmation_irreversible')}
    />
  );
}
